import { useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '../../components/Button';
import { TextInput } from '../../components/TextInput';
import { SignInLink } from '../auth/AcceptInviteLayout';
import { SignupError, UNKNOWN_MESSAGE, lookupSignup } from '../../services/signup';
import type { SignupLookupResponse } from '../../types/signup';
import { SignupHeader } from './SignupLayout';

type EmailFormData = { email: string };

/**
 * First step: the coach's school email, looked up anonymously. The server decides everything from
 * it (school, level, price); this step only reports what it said. Whether the
 * program is already taken is not revealed here: the verified create step reports that.
 */
export const SignupEmailStep = ({
  initialEmail,
  onResult,
}: {
  initialEmail?: string;
  onResult: (email: string, lookup: SignupLookupResponse) => void;
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const hintId = useId();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<EmailFormData>({
    mode: 'onSubmit',
    defaultValues: { email: initialEmail ?? '' },
  });

  const onSubmit = async ({ email }: EmailFormData) => {
    const trimmed = email.trim();
    setFormError('');
    setSubmitting(true);

    try {
      const lookup = await lookupSignup(trimmed);
      onResult(trimmed, lookup);
    } catch (error: unknown) {
      if (error instanceof SignupError && error.code === 'invalid_email') {
        setError('email', { message: error.message });
      } else {
        setFormError(error instanceof SignupError ? error.message : UNKNOWN_MESSAGE);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SignupHeader
        title="Sign up your program"
        subtitle="RosterPilot for college baseball and softball coaches."
        focusOnMount={false}
      />

      <form className="accept-invite-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {formError && (
          <div className="accept-invite-message accept-invite-message--error" role="alert">
            <p>{formError}</p>
          </div>
        )}

        <div>
          <TextInput
            label="School email"
            type="email"
            autoComplete="email"
            placeholder="coach@school.edu"
            error={errors.email?.message}
            disabled={submitting}
            {...(errors.email
              ? // TextInput wires its own error id into aria-describedby; passing the prop at all,
                // even as undefined, would override it.
                {}
              : { 'aria-describedby': hintId })}
            data-testid="signup-email-input"
            {...register('email', {
              required: 'Enter your school email address',
              pattern: { value: /\S+@\S+\.\S+/, message: 'Enter a valid email address.' },
            })}
          />
          {!errors.email && (
            <p className="accept-invite-hint" id={hintId}>
              We use your school email to find your program and its yearly price.
            </p>
          )}
        </div>

        <div className="accept-invite-actions">
          <Button type="submit" fullWidth loading={submitting} data-testid="signup-lookup-button">
            Continue
          </Button>
          <SignInLink>Already have an account? Sign in</SignInLink>
        </div>
      </form>
    </>
  );
};
